import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FeatureToggleDirective } from 'ish-core/directives/feature-toggle.directive';
import { AccountFacade } from 'ish-core/facades/account.facade';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { BasketTotal } from 'ish-core/models/basket-total/basket-total.model';
import { Basket } from 'ish-core/models/basket/basket.model';
import { PriceItemHelper } from 'ish-core/models/price-item/price-item.helper';
import { PriceHelper } from 'ish-core/models/price/price.model';
import { PricePipe } from 'ish-core/models/price/price.pipe';
import { BasketPromotionComponent } from 'ish-shared/components/basket/basket-promotion/basket-promotion.component';

/**
 * The Cost Summary Component displays a detailed summary of basket or order costs, respectively.
 *
 * @example
 * <ish-basket-cost-summary
 *   [totals]="basket.totals"
 * ></ish-basket-cost-summary>
 */
@Component({
  selector: 'ish-basket-cost-summary',
  templateUrl: './basket-cost-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BasketPromotionComponent,
    NgTemplateOutlet,
    NgbPopover,
    FaIconComponent,
    AsyncPipe,
    PricePipe,
    TranslateModule,
    DatePipe,
    FeatureToggleDirective,
  ],
})
export class BasketCostSummaryComponent implements OnInit {
  private accountFacade = inject(AccountFacade);
  private checkoutFacade = inject(CheckoutFacade); // DIAS-CUSTOMIZATION

  readonly totals = input.required<BasketTotal>();

  readonly basket = input<Basket>(); // DIAS-CUSTOMIZATION

  taxTranslation$: Observable<string>;
  isLoggedIn$: Observable<boolean>; // DIAS-CUSTOMIZATION
  isBasketPage$: Observable<boolean>; // DIAS-CUSTOMIZATION
  invert = PriceHelper.invert;

  ngOnInit() {
    this.isLoggedIn$ = this.accountFacade.isLoggedIn$; // DIAS-CUSTOMIZATION
    this.isBasketPage$ = this.checkoutFacade.getBasketPage$(); // DIAS-CUSTOMIZATION
    this.taxTranslation$ = this.accountFacade.userPriceDisplayType$.pipe(
      map(type => (type === 'net' ? 'checkout.tax.text' : 'checkout.tax.TaxesLabel.TotalOrderVat'))
    );
  }

  get hasPaymentCostsTotal(): boolean {
    const paymentCosts = PriceItemHelper.selectType(this.totals()?.paymentCostsTotal, 'gross');
    return !!paymentCosts && !!paymentCosts.value;
  }
}
